import { useState, useEffect } from 'react';
import {
  MapPinIcon,
  ClockIcon,
  UserGroupIcon,
  ArrowPathIcon,
  SparklesIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

export default function RouteOptimizer({
  datasetId,
  voterCount = 0,
  onOptimize,
  isLoading = false,
  currentSpec = null
}) {
  // Form state
  const [numCanvassers, setNumCanvassers] = useState(1);
  const [timeBudgetHours, setTimeBudgetHours] = useState(2);
  const [criteriaPrompt, setCriteriaPrompt] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [walkingSpeedMph, setWalkingSpeedMph] = useState(3.0);
  const [timePerDoorMinutes, setTimePerDoorMinutes] = useState(3.0);
  const [roundtrip, setRoundtrip] = useState(false);

  // Use current visualization spec's summary as criteria if available
  useEffect(() => {
    if (currentSpec?.summary && !criteriaPrompt) {
      setCriteriaPrompt(currentSpec.summary);
    }
  }, [currentSpec, criteriaPrompt]);

  // Calculate estimated doors per canvasser
  const estimatedDoorsPerCanvasser = Math.floor(
    (timeBudgetHours * 60) / (timePerDoorMinutes + 1) // +1 minute walking between doors avg
  );

  const totalEstimatedDoors = estimatedDoorsPerCanvasser * numCanvassers;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isLoading) return;

    // Convert visualization spec filters to backend format
    // Backend expects: {field: value} or {field: [values]} or {field: {min, max}}
    // Frontend has: [{field, operator, value}]
    let filter = null;
    if (currentSpec?.filters?.length > 0) {
      filter = {};
      currentSpec.filters.forEach(f => {
        if (f.operator === '=' || f.operator === 'equals') {
          filter[f.field] = f.value;
        } else if (f.operator === 'in' && Array.isArray(f.value)) {
          filter[f.field] = f.value;
        } else if (f.operator === '>=' || f.operator === 'gte') {
          filter[f.field] = { min: f.value };
        } else if (f.operator === '<=' || f.operator === 'lte') {
          filter[f.field] = { max: f.value };
        } else if (f.operator === '>' || f.operator === 'gt') {
          filter[f.field] = { min: f.value + 1 };
        } else if (f.operator === '<' || f.operator === 'lt') {
          filter[f.field] = { max: f.value - 1 };
        }
      });
      // If no valid filters were converted, set to null
      if (Object.keys(filter).length === 0) {
        filter = null;
      }
    }

    onOptimize({
      criteria_prompt: criteriaPrompt || null,
      filter: filter,
      num_canvassers: numCanvassers,
      time_budget_hours: timeBudgetHours,
      walking_speed_mph: walkingSpeedMph,
      time_per_door_minutes: timePerDoorMinutes,
      roundtrip: roundtrip
    });
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <MapPinIcon className="h-4 w-4" />
          Route Optimization
        </h3>
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <Cog6ToothIcon className="h-3 w-3" />
          {showAdvanced ? 'Hide' : 'Show'} Advanced
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Target Criteria */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            <span className="flex items-center gap-1">
              <SparklesIcon className="h-3 w-3" />
              Target Criteria (optional)
            </span>
          </label>
          <input
            type="text"
            value={criteriaPrompt}
            onChange={(e) => setCriteriaPrompt(e.target.value)}
            placeholder="e.g., voters 55+ who have voted 3+ times"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Leave blank to use all geocoded voters ({voterCount.toLocaleString()})
          </p>
        </div>

        {/* Number of Canvassers */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            <span className="flex items-center gap-1">
              <UserGroupIcon className="h-3 w-3" />
              Number of Canvassers
            </span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="1"
              max="20"
              value={numCanvassers}
              onChange={(e) => setNumCanvassers(parseInt(e.target.value))}
              className="flex-1"
            />
            <input
              type="number"
              min="1"
              max="100"
              value={numCanvassers}
              onChange={(e) => setNumCanvassers(Math.max(1, parseInt(e.target.value) || 1))}
              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-md text-center"
            />
          </div>
        </div>

        {/* Time Budget */}
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">
            <span className="flex items-center gap-1">
              <ClockIcon className="h-3 w-3" />
              Time Budget (hours per canvasser)
            </span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min="0.5"
              max="8"
              step="0.5"
              value={timeBudgetHours}
              onChange={(e) => setTimeBudgetHours(parseFloat(e.target.value))}
              className="flex-1"
            />
            <input
              type="number"
              min="0.5"
              max="12"
              step="0.5"
              value={timeBudgetHours}
              onChange={(e) => setTimeBudgetHours(Math.max(0.5, parseFloat(e.target.value) || 0.5))}
              className="w-16 px-2 py-1 text-sm border border-gray-300 rounded-md text-center"
            />
          </div>
        </div>

        {/* Advanced Options */}
        {showAdvanced && (
          <div className="pt-3 border-t border-gray-200 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Walking Speed (mph)
                </label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  step="0.5"
                  value={walkingSpeedMph}
                  onChange={(e) => setWalkingSpeedMph(parseFloat(e.target.value) || 3.0)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Time per Door (min)
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  step="0.5"
                  value={timePerDoorMinutes}
                  onChange={(e) => setTimePerDoorMinutes(parseFloat(e.target.value) || 3.0)}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={roundtrip}
                onChange={(e) => setRoundtrip(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded border-gray-300"
              />
              <span className="text-xs text-gray-700">
                Return to starting point (round trip)
              </span>
            </label>
          </div>
        )}

        {/* Estimates */}
        <div className="bg-gray-50 rounded-md p-3">
          <div className="text-xs font-medium text-gray-600 mb-2">Estimated Coverage</div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-gray-500">Per canvasser:</span>
              <span className="ml-1 font-medium text-gray-900">
                ~{estimatedDoorsPerCanvasser} doors
              </span>
            </div>
            <div>
              <span className="text-gray-500">Total:</span>
              <span className="ml-1 font-medium text-gray-900">
                ~{totalEstimatedDoors} doors
              </span>
            </div>
          </div>
          {totalEstimatedDoors > voterCount && voterCount > 0 && (
            <p className="mt-2 text-xs text-amber-600">
              Note: Estimated capacity exceeds available voters. Routes will be optimized for {voterCount.toLocaleString()} voters.
            </p>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isLoading || voterCount === 0}
          className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
            isLoading || voterCount === 0
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-blue-600 text-white hover:bg-blue-700'
          }`}
        >
          {isLoading ? (
            <>
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
              Optimizing Routes...
            </>
          ) : (
            <>
              <MapPinIcon className="h-4 w-4" />
              Generate Optimal Routes
            </>
          )}
        </button>
      </form>
    </div>
  );
}
